import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// ── Icons ──────────────────────────────────────────────────────────────────────

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function IconPlay() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}

function IconX() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  success: 'var(--success)',
  failed:  'var(--danger)',
  running: 'var(--warning)',
  pending: 'var(--text-muted)',
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || 'var(--text-muted)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 600, padding: '2px 8px',
      borderRadius: 999, background: `${color}22`, color,
      textTransform: 'capitalize',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {status || '—'}
    </span>
  );
}

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg-elevated)', borderRadius: 'var(--radius)',
      padding: '16px 20px', flex: '1 1 140px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text-primary)' }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  try { return new Date(val).toLocaleString(); } catch { return val; }
}

function fmtDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}m ${s}s`;
}

// ── Log modal ─────────────────────────────────────────────────────────────────

function LogModal({ run, onClose }) {
  const [fullRun, setFullRun] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/jobs/${run.job_id}/runs/${run.id}`)
      .then(r => setFullRun(r.data))
      .catch(() => setFullRun(run))
      .finally(() => setLoading(false));
  }, [run]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 860,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600 }}>Run #{run.id}</span>
            <StatusBadge status={run.status} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {fmtDate(run.started_at)}
              {run.duration_seconds != null && ` · ${fmtDuration(run.duration_seconds)}`}
            </span>
          </div>
          <button className="btn btn-ghost" style={{ padding: 4 }} onClick={onClose}>
            <IconX />
          </button>
        </div>

        {/* Result summary */}
        {fullRun?.result_summary && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Result Summary</div>
            <pre style={{
              fontSize: 12, color: 'var(--text-secondary)',
              background: 'var(--bg-elevated)', padding: 10,
              borderRadius: 'var(--radius)', overflow: 'auto', maxHeight: 100,
            }}>
              {JSON.stringify(fullRun.result_summary, null, 2)}
            </pre>
          </div>
        )}

        {/* Log */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <div className="spinner" />
            </div>
          ) : (
            <pre style={{
              fontSize: 12, lineHeight: 1.7,
              color: 'var(--text-secondary)', fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {fullRun?.log_text || '(no log output)'}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Runs tab ───────────────────────────────────────────────────────────────────

function RunsTab({ jobId }) {
  const [runs, setRuns]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const LIMIT = 20;

  const fetchRuns = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`/jobs/${jobId}/runs`, { params: { page: p, limit: LIMIT } });
      setRuns(res.data.data || []);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchRuns(1); }, [fetchRuns]);

  function handlePageChange(p) {
    setPage(p);
    fetchRuns(p);
  }

  return (
    <div>
      {loading && runs.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" />
        </div>
      ) : runs.length === 0 ? (
        <div className="empty-state">
          <h3>No runs yet</h3>
          <p>Trigger the job manually or wait for the schedule to fire</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Run #', 'Status', 'Triggered By', 'Started', 'Duration', 'Result'].map(h => (
                    <th key={h} style={{
                      padding: '10px 12px', textAlign: 'left',
                      fontSize: 11, color: 'var(--text-muted)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run.id} style={{
                    borderBottom: '1px solid var(--border)',
                    transition: 'background 150ms',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 12px', fontWeight: 600, fontSize: 13 }}>
                      #{run.id}
                    </td>
                    <td style={{ padding: '12px 12px' }}>
                      <StatusBadge status={run.status} />
                    </td>
                    <td style={{ padding: '12px 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
                      <span style={{
                        background: run.triggered_by === 'manual' ? 'var(--accent-light)' : 'var(--bg-elevated)',
                        color: run.triggered_by === 'manual' ? 'var(--accent)' : 'var(--text-secondary)',
                        padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      }}>
                        {run.triggered_by || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
                      {fmtDate(run.started_at)}
                    </td>
                    <td style={{ padding: '12px 12px', color: 'var(--text-secondary)', fontSize: 13 }}>
                      {fmtDuration(run.duration_seconds)}
                    </td>
                    <td style={{ padding: '12px 12px' }}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, color: 'var(--accent)' }}
                        onClick={() => setSelectedRun(run)}
                      >
                        View Log
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: 16, fontSize: 13, color: 'var(--text-secondary)',
          }}>
            <span>
              {total === 0 ? 'No runs' :
                `${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, total)} of ${total} run${total !== 1 ? 's' : ''}`}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn btn-secondary" style={{ padding: '5px 10px' }}
                disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                <IconChevronLeft />
              </button>
              <span style={{ minWidth: 80, textAlign: 'center' }}>
                Page {page} of {pages}
              </span>
              <button className="btn btn-secondary" style={{ padding: '5px 10px' }}
                disabled={page >= pages} onClick={() => handlePageChange(page + 1)}>
                <IconChevronRight />
              </button>
            </div>
          </div>
        </>
      )}

      {selectedRun && (
        <LogModal run={selectedRun} onClose={() => setSelectedRun(null)} />
      )}
    </div>
  );
}

// ── Stats tab ──────────────────────────────────────────────────────────────────

function StatsTab({ jobId }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`/jobs/${jobId}/stats`)
      .then(r => setStats(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="spinner" />
    </div>
  );

  if (error) return <div className="error-msg">{error}</div>;
  if (!stats) return null;

  return (
    <div>
      {/* Stat cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Runs"     value={stats.total_runs ?? 0} />
        <StatCard label="Success Rate"
          value={stats.success_rate_pct != null ? `${stats.success_rate_pct}%` : '—'}
          color={stats.success_rate_pct >= 80 ? 'var(--success)'
               : stats.success_rate_pct >= 50 ? 'var(--warning)' : 'var(--danger)'}
        />
        <StatCard label="Successes"      value={stats.success_count ?? 0} color="var(--success)" />
        <StatCard label="Failures"       value={stats.failed_count ?? 0}
          color={(stats.failed_count ?? 0) > 0 ? 'var(--danger)' : undefined}
        />
        <StatCard label="Avg Duration"
          value={stats.avg_duration_seconds != null ? fmtDuration(stats.avg_duration_seconds) : '—'}
        />
        <StatCard label="Next Run"       value={fmtDate(stats.next_run_at)} />
      </div>

      {/* Timeline */}
      <div style={{
        background: 'var(--bg-elevated)', borderRadius: 'var(--radius)',
        padding: '16px 20px',
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Run Timeline</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'Last Success', value: fmtDate(stats.last_success_at), color: 'var(--success)' },
            { label: 'Last Failure', value: fmtDate(stats.last_failure_at), color: 'var(--danger)' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: item.value === '—' ? 'var(--border)' : item.color,
                flexShrink: 0,
              }} />
              <span style={{ fontSize: 13, color: 'var(--text-muted)', minWidth: 100 }}>{item.label}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState('runs');
  const [triggering, setTriggering] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      const res = await axios.get(`/jobs/${id}`);
      setJob(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  async function handleTrigger() {
    if (triggering) return;
    setTriggering(true);
    try {
      await axios.post(`/jobs/${id}/trigger`);
      setTimeout(() => {
        setTriggering(false);
        fetchJob();
      }, 1500);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to trigger job');
      setTriggering(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div className="page-body">
      <div className="error-msg">{error}</div>
    </div>
  );

  if (!job) return null;

  const TABS = [
    { id: 'runs',  label: 'Run History' },
    { id: 'stats', label: 'Statistics' },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}
            onClick={() => navigate('/jobs')}
          >
            <IconArrowLeft /> Jobs
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>{job.name}</h2>
            {job.description && (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{job.description}</p>
            )}
          </div>
          <button
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleTrigger}
            disabled={triggering}
          >
            {triggering ? (
              <>
                <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                Triggering…
              </>
            ) : (
              <><IconPlay /> Run Now</>
            )}
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Job meta */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24,
        }}>
          {[
            { label: 'Type',     value: job.job_type },
            { label: 'Schedule', value: job.cron_expression },
            { label: 'Status',   value: job.enabled ? 'Enabled' : 'Disabled',
              color: job.enabled ? 'var(--success)' : 'var(--text-muted)' },
            { label: 'Next Run', value: fmtDate(job.next_run_at) },
          ].map(item => (
            <span key={item.label} style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 999, padding: '4px 12px', fontSize: 12,
              color: item.color || 'var(--text-secondary)',
            }}>
              <span style={{ color: 'var(--text-muted)' }}>{item.label}: </span>
              {item.value}
            </span>
          ))}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 0, borderBottom: '1px solid var(--border)',
          marginBottom: 24,
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px', background: 'none', border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: 13, cursor: 'pointer', marginBottom: -1,
                transition: 'color 150ms',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'runs'  && <RunsTab  jobId={id} />}
        {activeTab === 'stats' && <StatsTab jobId={id} />}
      </div>
    </div>
  );
}
